import React, { useState, useEffect, useRef, useMemo } from 'react';

interface TypingAnimationProps {
  text: string;
  speed?: number;
  onComplete?: () => void;
  className?: string;
}

// Helper function to strip markdown syntax for clean typing animation
function stripMarkdown(text: string): string {
  return text
    .replace(/\*\*(.*?)\*\*/g, '$1') // Remove **bold** 
    .replace(/\*(.*?)\*/g, '$1')     // Remove *italic*
    .replace(/`(.*?)`/g, '$1')       // Remove `code`
    .replace(/\[(.*?)\]\(.*?\)/g, '$1') // Remove [link](url)
    .replace(/#{1,6}\s/g, '')        // Remove # headers
    .replace(/>/g, '')               // Remove > blockquotes
    .replace(/\n\n/g, '\n')          // Reduce double line breaks
    .trim();
}

export function TypingAnimation({ text, speed = 50, onComplete, className = "" }: TypingAnimationProps) {
  const [displayedText, setDisplayedText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showCursor, setShowCursor] = useState(true);
  const hasCalledRef = useRef(false);

  // Strip markdown for typing animation
  const cleanText = useMemo(() => stripMarkdown(text), [text]);

  // Reset animation when text prop changes
  useEffect(() => {
    setDisplayedText('');
    setCurrentIndex(0);
    hasCalledRef.current = false; // Reset onComplete flag for new text
  }, [text]);

  useEffect(() => {
    if (currentIndex < cleanText.length) {
      const timer = setTimeout(() => {
        setDisplayedText(prev => prev + cleanText[currentIndex]);
        setCurrentIndex(prev => prev + 1);
      }, speed);

      return () => clearTimeout(timer);
    } else if (currentIndex === cleanText.length && onComplete && !hasCalledRef.current) {
      // Small delay before calling onComplete to let the user read
      hasCalledRef.current = true; // Mark as called to prevent multiple calls
      const timer = setTimeout(() => {
        onComplete();
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [currentIndex, cleanText, speed, onComplete]);

  // Cursor blinking effect
  useEffect(() => {
    const cursorTimer = setInterval(() => {
      setShowCursor(prev => !prev);
    }, 500);

    return () => clearInterval(cursorTimer);
  }, []);

  return (
    <span className={className}>
      {displayedText}
      {currentIndex < cleanText.length && (
        <span className={`inline-block w-0.5 h-4 bg-gray-400 ml-0.5 ${showCursor ? 'opacity-100' : 'opacity-0'}`} />
      )}
    </span>
  );
} 